/**
 * 退回控制棒落点计算输入。
 *
 * 文件定位：
 * 1. 当前文件只负责把指针位置解析为 FeatureManager 时间线 cursor；
 * 2. DOM 事件采集仍由 FeatureTree 组件负责，保持 UI 与规则计算解耦；
 * 3. 独立函数便于用单元测试锁定 SolidWorks 同款的“行上半区/下半区”拖放语义。
 */
export interface FeatureRollbackDropIndexInput {
  /** 目标节点在 FeatureManager 线性序列中的索引。 */
  featureManagerIndex: number;
  /** 当前指针的视口 Y 坐标。 */
  pointerClientY: number;
  /** 目标节点 DOM 包围盒顶部视口坐标。 */
  targetTop: number;
  /** 目标节点 DOM 包围盒高度。 */
  targetHeight: number;
}

/**
 * 退回控制棒可落放行。
 */
export interface FeatureRollbackDropTargetRow {
  /** 设计树节点 key。 */
  treeKey: string;
  /** 节点在 FeatureManager 线性序列中的索引。 */
  featureManagerIndex: number;
  /** 节点 DOM 包围盒顶部视口坐标。 */
  targetTop: number;
  /** 节点 DOM 包围盒高度。 */
  targetHeight: number;
}

/**
 * 退回控制棒解析后的落点。
 */
export interface FeatureRollbackResolvedDropTarget {
  /** 目标 FeatureManager cursor。 */
  cursorIndex: number;
  /** 用于显示插入线的目标节点 key。 */
  treeKey: string;
  /** 插入线相对目标节点的位置。 */
  placement: 'before' | 'after';
}

/**
 * 根据指针纵向位置解析退回控制棒目标 cursor。
 *
 * @param input 落点计算输入。
 * @returns 目标 FeatureManager cursor；上半区表示插到节点前，下半区表示插到节点后。
 */
export function resolveFeatureRollbackDropIndex(input: FeatureRollbackDropIndexInput): number {
  /**
   * DOM 在折叠、虚拟滚动或测试环境下可能返回 0 高度。
   * 这里只做最小归一化，避免异常高度把 cursor 推向不可预期位置。
   */
  const normalizedHeight = Math.max(0, input.targetHeight);
  const targetMiddleY = input.targetTop + normalizedHeight / 2;

  return input.featureManagerIndex + (input.pointerClientY > targetMiddleY ? 1 : 0);
}

/**
 * 读取行底边视口坐标。
 *
 * @param row 落点行。
 * @returns 底边 Y。
 */
function resolveFeatureRollbackDropRowBottom(row: FeatureRollbackDropTargetRow): number {
  return row.targetTop + Math.max(0, row.targetHeight);
}

/**
 * 判断渲染落点行相对权威时间线是否存在空洞。
 *
 * @param orderedRows 已按 featureManagerIndex 排序的落点行。
 * @param totalTimelineItemCount 权威时间线长度。
 * @returns 存在未渲染槽位时返回 true。
 */
function hasFeatureRollbackRenderedTimelineHole(
  orderedRows: readonly FeatureRollbackDropTargetRow[],
  totalTimelineItemCount: number,
): boolean {
  if (orderedRows.length === 0 || totalTimelineItemCount <= 0) {
    return false;
  }
  const maxRenderedIndex = orderedRows[orderedRows.length - 1].featureManagerIndex;
  if (maxRenderedIndex + 1 < totalTimelineItemCount) {
    return true;
  }
  for (let index = 0; index < orderedRows.length - 1; index += 1) {
    if (orderedRows[index + 1].featureManagerIndex > orderedRows[index].featureManagerIndex + 1) {
      return true;
    }
  }
  return orderedRows[0].featureManagerIndex > 0;
}

/**
 * 从当前可见 FeatureManager 时间线行中解析退回控制棒落点。
 *
 * 设计说明：
 * 1. 主路径按指针命中的行做上半区/下半区判定，保证「前一节点与草图之间」cursor 可达；
 * 2. 两行间隙等价于「上一行 after = 下一行 before」；
 * 3. `totalTimelineItemCount` 仅在渲染行存在真正空洞且指针低于最后可命中行时兜底到末尾，
 *    禁止连续邻行场景把中间档抬成时间线末尾。
 *
 * @param input 指针位置与可落放行集合。
 * @returns 解析后的落点；没有可落放行时返回 null。
 */
export function resolveFeatureRollbackDropTargetFromRows(input: {
  /** 当前指针的视口 Y 坐标。 */
  pointerClientY: number;
  /** 可作为退回控制棒落点参考的 FeatureManager 时间线行。 */
  rows: readonly FeatureRollbackDropTargetRow[];
  /** 权威 FeatureManager 退回序列长度；缺省时仅按渲染行推算。 */
  totalTimelineItemCount?: number;
}): FeatureRollbackResolvedDropTarget | null {
  if (input.rows.length === 0) {
    return null;
  }

  const orderedRows = [...input.rows].sort((left, right) => (
    left.featureManagerIndex - right.featureManagerIndex
  ));
  /**
   * 高度为 0 的行无法表达上/下半区，排除出命中集，但仍参与空洞检测（保留 featureManagerIndex）。
   */
  const hittableRows = orderedRows.filter((row) => row.targetHeight > 0);
  const rowsForHit = hittableRows.length > 0 ? hittableRows : orderedRows;
  const totalTimelineItemCount = Number.isFinite(input.totalTimelineItemCount)
    ? Math.max(0, Number(input.totalTimelineItemCount))
    : orderedRows[orderedRows.length - 1].featureManagerIndex + 1;
  const timelineHasHole = hasFeatureRollbackRenderedTimelineHole(
    orderedRows,
    totalTimelineItemCount,
  );

  const firstRow = rowsForHit[0];
  if (input.pointerClientY < firstRow.targetTop) {
    return {
      cursorIndex: firstRow.featureManagerIndex,
      treeKey: firstRow.treeKey,
      placement: 'before',
    };
  }

  for (let index = 0; index < rowsForHit.length; index += 1) {
    const row = rowsForHit[index];
    const rowBottom = resolveFeatureRollbackDropRowBottom(row);
    const nextRow = rowsForHit[index + 1];

    if (input.pointerClientY >= row.targetTop && input.pointerClientY <= rowBottom) {
      const cursorIndex = resolveFeatureRollbackDropIndex({
        featureManagerIndex: row.featureManagerIndex,
        pointerClientY: input.pointerClientY,
        targetTop: row.targetTop,
        targetHeight: row.targetHeight,
      });
      return {
        cursorIndex,
        treeKey: row.treeKey,
        placement: cursorIndex === row.featureManagerIndex ? 'before' : 'after',
      };
    }

    if (
      nextRow
      && input.pointerClientY > rowBottom
      && input.pointerClientY < nextRow.targetTop
    ) {
      return {
        cursorIndex: row.featureManagerIndex + 1,
        treeKey: row.treeKey,
        placement: 'after',
      };
    }
  }

  const lastHitRow = rowsForHit[rowsForHit.length - 1];
  const lastHitBottom = resolveFeatureRollbackDropRowBottom(lastHitRow);
  const afterLastHit = lastHitRow.featureManagerIndex + 1;
  /**
   * 仅当权威时间线仍有未渲染槽，且指针已低于最后可命中行时，才兜底推到末尾。
   * 连续邻行（特征→草图）场景必须停在 afterLastHit，以便单独退回草图。
   */
  const cursorIndex = input.pointerClientY > lastHitBottom
    && timelineHasHole
    && totalTimelineItemCount > afterLastHit
    ? totalTimelineItemCount
    : afterLastHit;

  return {
    cursorIndex,
    treeKey: lastHitRow.treeKey,
    placement: 'after',
  };
}
